import { useContext, useEffect, useState } from "react";
import { Dialog } from "cerulean";
import { Button, Row, TextInput } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";

export type ApprovalExpirationDialog = {
  isOpen: boolean;
  handleClose: () => void;
  handleSave: (days: number) => Promise<void>;
  gracePeriodDays: number | null;
};

const ApprovalExpirationDialog = ({
  isOpen,
  handleClose,
  handleSave,
  gracePeriodDays,
}: ApprovalExpirationDialog) => {
  const { l10n } = useLocalization();
  const { max_days_approval_grace_period: maxDays } = useContext(
    InstitutionSettingsContext
  );
  const [days, setDays] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const MAX_APPROVAL_DAYS = maxDays ?? 0;

  useEffect(() => {
    if (isOpen) {
      setDays(gracePeriodDays?.toString() ?? "");
      setErrorMessage(null);
    }
  }, [isOpen, gracePeriodDays]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    // allow empty so user can clear the field
    if (value === "") {
      setDays("");
      setErrorMessage(null);
      return;
    }

    const numericInput = Number(value);
    if (numericInput > MAX_APPROVAL_DAYS) {
      setErrorMessage(`Cannot be longer than ${MAX_APPROVAL_DAYS} days.`);
    } else {
      setErrorMessage(null);
    }

    setDays(value);
  };

  const handleClickSave = async () => {
    try {
      await handleSave(Number(days));
      handleClose();
    } catch (err) {
      console.error("Failed to save expiration days", err);
      setErrorMessage("Failed to save. Please try again.");
    }
  };

  return (
    <Dialog
      width="475px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      title={l10n.getString("approval-expiration-title")}
      footer={
        <Row alignItems="center">
          <Row.Item>
            <Button kind="plain" onClick={handleClose}>
              {l10n.getString("button-cancel")}
            </Button>
          </Row.Item>
          <Row.Item shrink>
            <Button
              onClick={handleClickSave}
              disabled={Boolean(errorMessage || !days)}
            >
              {l10n.getString("button-save")}
            </Button>
          </Row.Item>
        </Row>
      }
    >
      <p>{l10n.getString("approval-expiration-dialog-content-description")}</p>
      <p>{l10n.getString("approval-expiration-dialog-content-cta")}</p>
      <TextInput
        type="number"
        label="Days before expiration"
        value={days}
        onChange={handleOnChange}
        error={errorMessage}
      />
    </Dialog>
  );
};

export default ApprovalExpirationDialog;
